/**
 * useDeliberationData — shared War Room deliberation fetch + trigger + poll.
 *
 * Extracted from WarRoom.tsx so the raw `/deliberation/{id}` page and the
 * accountant workspace Stage-6 panel share ONE copy of the fleet-first fetch,
 * the defensive issues/events array normalization (its absence black-pages the
 * render), and the rerunEngines-before-runFleet trigger ordering. Behavior is
 * byte-equivalent to the original inline logic — pure extraction, no new
 * behavior. See docs/plans/2026-08-23-warroom-stage6-design-synthesis.md.
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getFleetDeliberation,
  getDeliberation,
  getSummary,
  getTriggerStatus,
  rerunEngines,
  runFleet,
  getFleetStatus,
  type CaseDeliberation,
  type DeliberationSummary,
  type AgentToolTiming,
} from '@/services/deliberationApi';

export type AnalysisState = 'idle' | 'triggering' | 'running' | 'complete' | 'failed';

export interface UseDeliberationData {
  deliberation: CaseDeliberation | null;
  summary: DeliberationSummary | null;
  loading: boolean;
  error: string | null;
  analysisState: AnalysisState;
  analysisMessage: string;
  agentTiming: Record<string, AgentToolTiming> | null;
  /** Re-fetch the deliberation + summary. */
  refetch: () => void;
  /** Refresh engines then start a fleet run, then poll to completion. */
  triggerAnalysis: () => void;
}

export function useDeliberationData(reportId: string | undefined): UseDeliberationData {
  const [deliberation, setDeliberation] = useState<CaseDeliberation | null>(null);
  const [summary, setSummary] = useState<DeliberationSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [analysisState, setAnalysisState] = useState<AnalysisState>('idle');
  const [analysisMessage, setAnalysisMessage] = useState<string>('');
  const [agentTiming, setAgentTiming] = useState<Record<string, AgentToolTiming> | null>(null);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const fetchData = useCallback(async () => {
    if (!reportId) return;
    setLoading(true);
    setError(null);
    try {
      // Prefer the completed agent-fleet run (six seats, incl. the adversarial
      // defendant-attorney). Fall back to the classic case_deliberations doc when
      // no completed fleet run exists (404 → getFleetDeliberation throws).
      const [delibRaw, summ] = await Promise.all([
        getFleetDeliberation(reportId).catch(() => getDeliberation(reportId)),
        getSummary(reportId),
      ]);
      // Defensive normalize: consumers read deliberation.issues and .events
      // directly, so guarantee both are arrays regardless of source (the fleet doc
      // omits some fields the classic doc carries). Missing either field would
      // crash the render (black page).
      const delib = {
        ...delibRaw,
        issues: (delibRaw as any).issues ?? [],
        events: (delibRaw as any).events ?? [],
      } as CaseDeliberation;
      setDeliberation(delib);
      setSummary(summ);
      setAnalysisState('idle');
      // Best-effort tool-timing (observability-only; silence failures).
      getTriggerStatus(reportId)
        .then((s) => { if (s.agent_timing) setAgentTiming(s.agent_timing); })
        .catch(() => {/* observability only */});
    } catch (err: any) {
      setError(err?.message || 'Failed to load deliberation data');
    } finally {
      setLoading(false);
    }
  }, [reportId]);

  const startPolling = useCallback(() => {
    if (!reportId || pollRef.current) return;
    pollRef.current = setInterval(async () => {
      try {
        const status = await getFleetStatus(reportId);
        if (status.status === 'completed') {
          setAnalysisState('complete');
          setAnalysisMessage('Fleet analysis complete — results updated');
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
          fetchData();
        } else if (status.status === 'failed' || status.status === 'orphaned') {
          setAnalysisState('failed');
          setAnalysisMessage(status.error || `Fleet analysis ${status.status}`);
          if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
        }
      } catch {
        // Polling error — keep trying.
      }
    }, 15000);
  }, [reportId, fetchData]);

  const triggerAnalysis = useCallback(async () => {
    if (!reportId) return;
    setAnalysisState('triggering');
    setAnalysisMessage('Refreshing calculation engines…');
    try {
      // Engines FIRST: the fleet's agents read engine_outputs, and skipping this
      // silently carries pre-upload figures into the new run (the real d6d88aea
      // "Toast 2023" failure). Engine refresh completes in seconds and blocks.
      await rerunEngines(reportId);
      const res = await runFleet(reportId);
      setAnalysisState('running');
      setAnalysisMessage(`Fleet analysis running (${res.run_id}) — typically 25–40 minutes…`);
      startPolling();
    } catch (err: any) {
      const msg = String(err?.message || '');
      setAnalysisState('failed');
      setAnalysisMessage(
        msg.includes('409') || msg.toLowerCase().includes('in flight')
          ? 'A fleet run is already in flight for this report — wait for it to finish.'
          : msg || 'Failed to start fleet analysis',
      );
    }
  }, [reportId, startPolling]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    return () => {
      if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    };
  }, []);

  return {
    deliberation,
    summary,
    loading,
    error,
    analysisState,
    analysisMessage,
    agentTiming,
    refetch: fetchData,
    triggerAnalysis,
  };
}

export default useDeliberationData;
